import { isRef, Ref } from 'vue'

/**
 * Merges updates into a source object, handling both regular objects and refs.
 * 
 * @param source - The source object or ref to merge into
 * @param updates - Partial updates to apply
 * @returns A new object with updates applied
 * 
 * @example
 * const state = { name: 'John', age: 30 }
 * const updated = mergeUpdates(state, { age: 31 })
 * // { name: 'John', age: 31 }
 */
export function mergeUpdates<T extends object>(
  source: T | Ref<T>,
  updates: Partial<T>
): T {
  if (isRef(source)) {
    return { ...source.value, ...updates }
  }
  return { ...source, ...updates }
}

/**
 * Handles API errors consistently across the browser extension feature.
 * Logs the error with context.
 * 
 * @param error - The error that occurred
 * @param context - A description of what operation failed
 */
export function handleApiError(error: unknown, context: string): void {
  const message = error instanceof Error ? error.message : String(error)
  console.error(`[BrowserExtension] ${context}:`, message)
}

/**
 * Truncates a long ID string for display, showing start and end portions.
 * 
 * @param id - The ID string to truncate
 * @param startChars - Number of characters to show at start (default: 8)
 * @param endChars - Number of characters to show at end (default: 8)
 * @returns The truncated ID string
 * 
 * @example
 * truncateId('abc123def456ghi789') // 'abc123de...hi789'
 * truncateId('short', 4, 4) // 'short' (no truncation if shorter than sum)
 */
export function truncateId(
  id: string,
  startChars: number = 8,
  endChars: number = 8
): string {
  if (!id) return ''
  if (id.length <= startChars + endChars) return id
  return `${id.substring(0, startChars)}...${id.substring(id.length - endChars)}`
}

/**
 * Safely extracts an error message from an unknown error.
 * 
 * @param error - Unknown error value
 * @returns Error message string
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return String(error)
}
